import { Events } from '@/services/Events';
import { textScaleToFontsize } from '@/commons/fabricjs/FabricTextHelper';
import { imageQualityChecker } from '@/utils/imageQualityChecker';
import { FONT_SIZE_MULTIPLIER, WARNING_MINIMUM_DPI } from '@/littleWedding/projectConstantes';
import { useTemplateStore } from '@/store/template.store';
import { CUTTING_GROUP_NAME } from '@/littleWedding/fabricGroupsName';
import { getFabricGroupById } from '@/commons/fabricjs/FabricGroupHelper';
import { Point, type TPointerEvent, type BasicTransformEvent, FabricObject, IText, Group, Canvas } from 'fabric';
import { storeToRefs } from 'pinia';
import type { IFabricImageCustom } from '@/typescript/interfaces/IFabricImageCustom';
import type { IFabricTextCustom } from '@/typescript/interfaces/IFabricTextCustom';
import type { ITextboxCustom } from '@/typescript/interfaces/ITextboxCustom';
import type { WFabricWorld } from '@/littleWedding/WFabricWorld';
import type { IGroup } from '@/typescript/interfaces/IGroup';

// Object level font weight is 'bold' | 'normal' at creation and '700' | '300' after an edit
const BOLD_FONT_WEIGHTS: (number | string)[] = ['bold', '700', 700];

/**
 * Update object callback - usefull to trigger event when object is modified inside fabric canvas
 * @param e
 * @this GFabricWorld
 */
function onUpdateObjectCallback(e: BasicTransformEvent<TPointerEvent> & { target: FabricObject }) {
    switch (e.target.type) {
        case 'group':
            onUpdateCuttingGroupPath(e.target as unknown as Group);
            break;
        case 'image':
            onUpdateImageCallback(e.target as IFabricImageCustom);
            break;
        case 'text':
            onUpdateTextCallback(e.target as IFabricTextCustom);
            break;
        case 'textbox':
            onUpdateTextBoxCallback(e.target as ITextboxCustom);
            break;
        default:
            break;
    }
}

function onUpdateImageCallback(fImage: IFabricImageCustom) {
    const { angle, frameSize, height, id, left, scaleX, scaleY, selectable, top, width } = fImage;
    const hasQualityWarning = imageQualityChecker({ width, height, scaleX, scaleY, dpiCheck: WARNING_MINIMUM_DPI });
    const transform = {
        position: {
            x: left,
            y: top,
        },
        angle,
        scale: {
            x: scaleX,
            y: scaleY,
        },
    };
    Events.emit('image-transformed', { id, transform, editable: selectable, frameSize, hasQualityWarning });
}

function onUpdateTextBoxCallback(fTextbox: ITextboxCustom) {
    fTextbox.set('angle', Math.round(fTextbox.angle));
    fTextbox.set('left', Math.round(fTextbox.left));
    fTextbox.set('top', Math.round(fTextbox.top));
    fTextbox.setCoords();
    fTextbox.set('dirty', true);

    const {
        angle,
        fill,
        fontFamily,
        fontSize,
        fontStyle,
        fontWeight,
        frameSize,
        height,
        id,
        left,
        scaleX,
        scaleY,
        styles,
        text,
        top,
        underline,
        width,
    } = fTextbox;
    const transform = {
        position: {
            x: left,
            y: top,
        },
        angle,
        scale: {
            x: scaleX,
            y: scaleY,
        },
        size: {
            width,
            height,
        },
    };

    Events.emit('textbox-transformed', {
        id,
        transform,
        text,
        frameSize,
        // Editing the text can move character level styles to object level (see WTextbox) or shift
        // them: keep the field aligned with the fabric object, the edit form reads the field.
        font: fontFamily,
        color: typeof fill === 'string' ? fill : undefined,
        fontSize: fontSize / FONT_SIZE_MULTIPLIER,
        isBold: BOLD_FONT_WEIGHTS.includes(fontWeight),
        isItalic: fontStyle === 'italic',
        isUnderlined: underline,
        styles,
    });
}

function onUpdateTextCallback(fText: IFabricTextCustom) {
    textScaleToFontsize(fText);
    fText.set('angle', Math.round(fText.angle));
    fText.set('left', Math.round(fText.left));
    fText.set('top', Math.round(fText.top));
    fText.setCoords();
    fText.set('dirty', true);

    const { fontSize, frameSize, id, selectable } = fText;
    const transform = {
        position: {
            x: fText.left,
            y: fText.top,
        },
        angle: fText.angle,
        scale: {
            x: fText.scaleX,
            y: fText.scaleY,
        },
    };
    Events.emit('text-transformed', {
        id,
        transform,
        editable: selectable,
        fontSize: Math.round(fontSize / FONT_SIZE_MULTIPLIER),
        frameSize,
    });
}

function onUpdateCuttingGroupPath(cuttingGroupPath: IGroup) {
    if (cuttingGroupPath?.id !== CUTTING_GROUP_NAME) {
        return;
    }
    cuttingGroupPath.getObjects().forEach((obj) => {
        if ((obj as IGroup)?.id) {
            const transform = {
                position: {
                    x: cuttingGroupPath.left,
                    y: cuttingGroupPath.top,
                },
                angle: cuttingGroupPath.angle,
                scale: {
                    x: cuttingGroupPath.scaleX,
                    y: cuttingGroupPath.scaleY,
                },
            };
            Events.emit('image-transformed', {
                id: (obj as IGroup).id,
                transform,
                editable: true,
                frameSize: { width: 0, height: 0 },
                hasQualityWarning: false,
            });
        }
    });
}

function objectMovingConstrainCallback<T extends WFabricWorld>(
    fabricWorld: T,
    boundingBoxTest: {
        br: Point;
        tl: Point;
    },
) {
    const OFFSET = 3; // usefull to be sure than point is not overlapping with constrain size
    return (options: BasicTransformEvent & { target: FabricObject }) => {
        if (!fabricWorld.fabricCanvas) {
            return;
        }
        const objectToTest = (fabricWorld.fabricCanvas.getObjects() as (IFabricImageCustom | IFabricTextCustom | ITextboxCustom)[]).filter(
            (o) => fabricWorld.objectConstrainId.includes(o.id),
        );
        if (objectToTest.includes(options.target as IFabricImageCustom | IFabricTextCustom | ITextboxCustom)) {
            if (options.target.left < boundingBoxTest.tl.x) {
                options.target.set('left', boundingBoxTest.tl.x + OFFSET);
                options.target.set('top', boundingBoxTest.tl.y + OFFSET);
                options.target.set('dirty', true);
                options.target.setCoords();
                return;
            }
            if (options.target.left + options.target.width - OFFSET > boundingBoxTest.br.x) {
                options.target.set('left', boundingBoxTest.br.x - options.target.width);
                options.target.set('top', boundingBoxTest.br.y - options.target.height);
                options.target.set('dirty', true);
                options.target.setCoords();
                return;
            }
        }
    };
}

function onFabricCanvasScaleCallback(object: { target: FabricObject }) {
    const target = object.target as IText;
    if (target?.text) {
        const scaledWidth = target.getScaledWidth();

        target.set({
            width: scaledWidth,
            scaleX: 1,
            scaleY: 1,
        });
    }
}

function onOneTextBoxSelectedCallback(options: { selected: FabricObject[] }) {
    const { selectedTextIndexes } = storeToRefs(useTemplateStore());

    if (selectedTextIndexes.value) {
        selectedTextIndexes.value = undefined;
    }
    if (options.selected.length !== 1) {
        return;
    }
    const text = options.selected[0];

    if (!(text instanceof IText)) {
        return;
    }

    // This callback runs on every selection:created / selection:updated, so drop the previous
    // listeners first to avoid stacking one closure per selection on the same object.
    text.off('selection:changed');
    text.off('changed');

    text.on('selection:changed', () => {
        const start = text.selectionStart;
        const end = text.selectionEnd;

        if (start === end) {
            selectedTextIndexes.value = undefined;
        } else {
            selectedTextIndexes.value = { start, end };
        }
    });

    // Editing the text invalidates the indexes captured on the previous content, and the store is
    // updated on the next transform event only - so clear them before anyone can read them.
    text.on('changed', () => {
        selectedTextIndexes.value = undefined;
    });
}

function setCuttingPathVisibility(fabricCanvas: Canvas, opacity: number) {
    const cuttingGroup = getFabricGroupById(fabricCanvas, CUTTING_GROUP_NAME);
    if (!cuttingGroup) {
        return;
    }
    // check if cutting group exist
    const isClipped = cuttingGroup?.getObjects().every((obj) => !obj.clipPath);
    if (!isClipped) {
        return;
    }
    cuttingGroup.opacity = opacity;
}

export {
    objectMovingConstrainCallback,
    onFabricCanvasScaleCallback,
    onOneTextBoxSelectedCallback,
    onUpdateImageCallback,
    onUpdateObjectCallback,
    onUpdateTextBoxCallback,
    onUpdateTextCallback,
    setCuttingPathVisibility,
};
